import { useRequiredContext } from "@redotech/react-util/context";
import FileIcon from "@redotech/redo-icons/file-02.svg";
import FileIconFilled from "@redotech/redo-icons/file-02_filled.svg";
import PackageCheckIcon from "@redotech/redo-icons/package-check.svg";
import PackageCheckFilledIcon from "@redotech/redo-icons/package-check_filled.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import PackageIconFilled from "@redotech/redo-icons/package_filled.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-ccw-03_filled.svg";
import RefreshIconOutline from "@redotech/redo-icons/refresh-cw-02.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import ReportIconFilled from "@redotech/redo-merchant-inventory-app/icons/report-icon-filled.svg";
import ReportIcon from "@redotech/redo-merchant-inventory-app/icons/report-icon.svg";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useLocation } from "react-router-dom";
import { InventoryManagementSuiteStatusesContext } from "../../../../redo-suite/inventory-cloud";
import { PurchaseOrdersItem } from "./purchase-orders";
import { ShipmentsItem } from "./shipments";

export const InventoryItem = memo(function InventoryItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const location = useLocation();
  const team = useRequiredContext(TeamContext);

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );

  // Check if marketplace is enabled for this team
  const isMarketplaceEnabled = team.settings.recommerce?.marketplace ?? false;

  // Check if forecasting is enabled for this team
  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;

  // Check if catalog is enabled for this team
  const isCatalogEnabled = team.settings.ims?.catalogEnabled ?? false;

  // Check if pricing rules is enabled for this team
  // TODO: Add pricingRulesEnabled to ImsSettings when backend supports it
  const isPricingRulesEnabled = team.settings.ims?.pricingRulesEnabled ?? false;

  // Check if Redo Managed Returns is enabled for this team
  const isManagedReturnsEnabled = team.settings.redoManagedReturns?.enabled;

  const isRecommerceChildSelected =
    location.pathname.includes("/liquidation/") ||
    location.pathname.endsWith("/liquidation");

  // Check if any catalog child routes are selected
  const isCatalogChildSelected =
    location.pathname.includes("/catalog/") ||
    location.pathname.endsWith("/catalog");

  // Check if any inventory child routes are selected
  const isInventoryChildSelected =
    location.pathname.includes("/inventory/") ||
    location.pathname.endsWith("/inventory");

  // Check if suppliers routes are selected
  const isSuppliersSelected =
    location.pathname.includes("/suppliers") ||
    location.pathname.endsWith("/suppliers");

  // Check if managed returns routes are selected
  const isManagedReturnsSelected =
    location.pathname.includes("/managed-returns") ||
    location.pathname.endsWith("/managed-returns");

  return (
    <>
      {isCatalogEnabled && (
        <RedoSecondaryNavItem
          action={{ type: "button" }}
          anyChildSelected={isCatalogChildSelected}
          FilledIcon={FileIconFilled}
          friendlyName="Catalog"
          id="catalog"
          isFolder
          UnfilledIcon={FileIcon}
        >
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: `${routes.inventoryManagementBaseUrl}/catalog/products`,
            }}
            friendlyName="Products"
            id="catalog-products"
          />
          {/* Only available to the Redo Outlet team */}
          {team._id === "68800d96229f8fc10f4dc1ce" && (
            <RedoSecondaryNavItem
              action={{
                type: "link",
                href: `${routes.inventoryManagementBaseUrl}/catalog/consignment`,
              }}
              friendlyName="Consignment"
              id="catalog-consignment"
            />
          )}
        </RedoSecondaryNavItem>
      )}

      {imsEnabled && (
        <RedoSecondaryNavItem
          action={{ type: "button" }}
          anyChildSelected={isInventoryChildSelected}
          FilledIcon={PackageIconFilled}
          friendlyName="Inventory"
          id="inventory"
          isFolder
          UnfilledIcon={PackageIcon}
        >
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: `${routes.inventoryManagementBaseUrl}/products`,
            }}
            friendlyName="Inventory Levels"
            id="inventory-levels"
          />
          {isPricingRulesEnabled && (
            <RedoSecondaryNavItem
              action={{
                type: "link",
                href: `${routes.inventoryManagementBaseUrl}/pricing-rules`,
              }}
              friendlyName="Pricing Rules"
              id="pricing-rules"
            />
          )}
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: `${routes.inventoryManagementBaseUrl}/adjustments`,
            }}
            friendlyName="Inventory Adjustments"
            id="inventory-adjustments"
          />
          {isForecastingEnabled && (
            <>
              <RedoSecondaryNavItem
                action={{
                  type: "link",
                  href: `${routes.inventoryManagementBaseUrl}/forecasting`,
                }}
                friendlyName="Forecasting"
                id="forecasting"
              />
              <RedoSecondaryNavItem
                action={{
                  type: "link",
                  href: `${routes.inventoryManagementBaseUrl}/suppliers`,
                }}
                anyChildSelected={isSuppliersSelected}
                friendlyName="Suppliers"
                id="suppliers"
              />
              <PurchaseOrdersItem />
              <ShipmentsItem />
            </>
          )}
        </RedoSecondaryNavItem>
      )}

      {imsEnabled && (
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: `${routes.inventoryManagementBaseUrl}/reports`,
          }}
          FilledIcon={ReportIconFilled}
          friendlyName="Reporting"
          id="reporting"
          UnfilledIcon={ReportIcon}
        />
      )}

      {isManagedReturnsEnabled && (
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: `${routes.inventoryManagementBaseUrl}/managed-returns`,
          }}
          anyChildSelected={isManagedReturnsSelected}
          FilledIcon={PackageCheckFilledIcon}
          friendlyName="Managed Returns"
          id="managed-returns"
          UnfilledIcon={PackageCheckIcon}
        />
      )}

      {isMarketplaceEnabled && (
        <RedoSecondaryNavItem
          action={{ type: "button" }}
          anyChildSelected={isRecommerceChildSelected}
          FilledIcon={RefreshIcon}
          friendlyName="Recommerce"
          id="recommerce"
          isFolder
          UnfilledIcon={RefreshIconOutline}
        >
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: `${routes.inventoryManagementBaseUrl}/liquidation/dashboard`,
            }}
            friendlyName="Dashboard"
            id="liquidation-dashboard"
          />
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: `${routes.inventoryManagementBaseUrl}/liquidation/products`,
            }}
            friendlyName="All listings"
            id="liquidation-all-products"
          />
        </RedoSecondaryNavItem>
      )}
    </>
  );
});
